import React, { useEffect, useState, useMemo } from 'react';
import { http } from '../../lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { 
  Clock, TrendingUp, Calendar, BarChart3, AlertTriangle, CheckCircle, Activity,
  ArrowLeft, ChevronLeft, ChevronRight, FileText, User
} from 'lucide-react';

export default function MisHoras() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(true);

  const mesesNombres = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const formatearHoras = (h) => {
    if (h === null || h === undefined || isNaN(h)) return '0h 0m';
    const horas = Math.floor(h);
    const minutos = Math.round((h - horas) * 60);
    return `${horas}h ${minutos}m`;
  };

  useEffect(() => { cargarDetalle(); }, [anio, mes]);
  useEffect(() => { cargarHistorico(); }, []);

  const cargarDetalle = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`/cliente/mis-horas/detalle?anio=${anio}&mes=${mes}`);
      setDetalle(res.data);
    } catch (err) {
      console.error('Error cargando detalle:', err);
      setError(err.response?.data?.detail || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const cargarHistorico = async () => {
    try {
      const res = await http.get('/cliente/mis-horas/historico?cantidad=12');
      setHistorico(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error cargando histórico:', err);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const navegarMes = (dir) => {
    let m = mes + dir;
    let a = anio;
    if (m > 12) { m = 1; a++; }
    if (m < 1) { m = 12; a--; }
    setMes(m);
    setAnio(a);
  };

  const esHoy = () => {
    const now = new Date();
    return anio === now.getFullYear() && mes === now.getMonth() + 1;
  };

  const resumen = detalle?.resumen || {};
  const ordenes = detalle?.ordenes || [];

  const porcentajeUso = resumen.porcentaje_uso || 0;
  const estadoUso = porcentajeUso >= 90 ? { color: 'text-red-600', bg: 'bg-red-500', msg: 'Crítico', icon: AlertTriangle } :
    porcentajeUso >= 75 ? { color: 'text-amber-600', bg: 'bg-amber-500', msg: 'Alto', icon: Activity } :
    porcentajeUso >= 50 ? { color: 'text-emerald-600', bg: 'bg-emerald-500', msg: 'Moderado', icon: TrendingUp } :
    { color: 'text-emerald-600', bg: 'bg-emerald-500', msg: 'Óptimo', icon: CheckCircle };

  const handleBarClick = (data) => {
    if (data && data.activePayload?.length) {
      const { anio: a, mes_num: m } = data.activePayload[0].payload;
      setAnio(a);
      setMes(m);
    }
  };


  if (error && !detalle) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
          <AlertTriangle className="w-6 h-6 text-red-600 mb-2" />
          <h3 className="text-lg font-semibold text-red-800">Error al cargar</h3>
          <p className="text-red-700 mt-1">{error}</p>
          <button onClick={cargarDetalle} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'dashboard_cliente' }))}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Mis Horas</h1>
            <p className="text-gray-600">Control detallado de horas contratadas y consumidas</p>
          </div>
        </div>

        {/* Navegador de mes */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center justify-between">
          <button onClick={() => navegarMes(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div className="text-center">
            <h2 className="text-lg font-bold text-gray-900">{mesesNombres[mes]} {anio}</h2>
            {!esHoy() && (
              <button onClick={() => { setAnio(new Date().getFullYear()); setMes(new Date().getMonth() + 1); }}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                Ir al mes actual
              </button>
            )}
          </div>
          <button onClick={() => navegarMes(1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={esHoy()}>
            <ChevronRight size={20} className={esHoy() ? 'text-gray-300' : 'text-gray-600'} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <KpiCard label="Contratadas" value={formatearHoras(resumen.horas_contratadas)} icon={BarChart3} color="green" />
              <KpiCard label="Computadas" value={formatearHoras(resumen.horas_computadas)} icon={Clock} color="blue"
                sub={resumen.horas_extras_horario > 0 ? `(${formatearHoras(resumen.horas_extras_horario)} extra)` : null} />
              <KpiCard label="Disponibles" value={formatearHoras(resumen.horas_restantes)} icon={TrendingUp} color="purple" />
              <KpiCard label={resumen.horas_excedidas > 0 ? 'Excedidas' : 'Estado'}
                value={resumen.horas_excedidas > 0 ? formatearHoras(resumen.horas_excedidas) : estadoUso.msg}
                icon={estadoUso.icon} color={resumen.horas_excedidas > 0 ? 'red' : 'emerald'} />
            </div>

            {/* Progress bar */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Uso del período</span>
                <span className="font-semibold">{porcentajeUso}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className={`h-3 rounded-full transition-all duration-500 ${estadoUso.bg}`}
                  style={{ width: `${Math.min(porcentajeUso, 100)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{formatearHoras(resumen.horas_computadas)} usadas</span>
                <span>{formatearHoras(resumen.horas_contratadas)} contratadas</span>
              </div>
            </div>

            {/* Tabla de órdenes del mes */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Órdenes del período ({ordenes.length})
                </h3>
              </div>
              {ordenes.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Sin órdenes en este período</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Orden</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Técnico</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horario</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cómputo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Firmante</th>

                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ordenes.map((o) => {
                        const tieneExtras = o.horas_extras_minutos > 0;
                        const formatHora = (h) => {
                          if (!h) return '-';
                          try { return h.includes('T') ? h.split('T')[1].substring(0,5) : h.substring(0,5); }
                          catch { return h; }
                        };
                        return (
                          <tr key={o.orden_id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{o.numero_orden}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {o.fecha ? new Date(o.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap capitalize">
                              <span className="flex items-center gap-1"><User size={12} className="text-gray-400" />{o.nombre_tecnico || 'Sin asignar'}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {formatHora(o.hora_inicio)} - {formatHora(o.hora_fin)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{o.duracion_formato}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`text-sm font-semibold ${tieneExtras ? 'text-orange-600' : 'text-gray-900'}`}>
                                {o.duracion_computada_formato}
                              </span>
                              {tieneExtras && (
                                <span className="text-[10px] text-orange-500 ml-1">×2</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px]">
                              <span className="line-clamp-2 whitespace-normal break-words">{o.descripcion || '-'}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 max-w-[120px] truncate">{o.firmante || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr className="font-semibold text-sm">
                        <td className="px-4 py-3 text-gray-700" colSpan={4}>Totales</td>
                        <td className="px-4 py-3 text-gray-700">{formatearHoras(resumen.horas_trabajadas)}</td>
                        <td className="px-4 py-3 text-emerald-700">{formatearHoras(resumen.horas_computadas)}</td>
                        <td colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Gráfico histórico */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Evolución Mensual</h3>
          <p className="text-xs text-gray-500 mb-4">Haga clic en una barra para ver el detalle del mes</p>
          {loadingHistorico ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" />
            </div>
          ) : historico.length === 0 ? (
            <p className="text-center text-gray-400 py-8">Sin datos históricos</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={historico} margin={{ top: 5, right: 10, left: -20, bottom: 5 }} onClick={handleBarClick}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="mes_corto" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: 12 }}
                  formatter={(value, name) => [
                    `${Number(value).toFixed(1)}h`,
                    name === 'horas_computadas' ? 'Computadas' : 'Contratadas'
                  ]}
                  labelFormatter={(l, p) => p?.[0]?.payload?.mes || l}
                />
                <Bar dataKey="horas_contratadas" fill="#e0e7ff" radius={[4,4,0,0]} name="horas_contratadas" />
                <Bar dataKey="horas_computadas" radius={[4,4,0,0]} name="horas_computadas" cursor="pointer">
                  {historico.map((entry, index) => (
                    <Cell key={`c-${index}`}
                      fill={entry.es_mes_actual ? '#3b82f6' : entry.porcentaje >= 90 ? '#ef4444' : entry.porcentaje >= 75 ? '#f59e0b' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="flex items-center gap-6 mt-3 justify-center text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-teal-200 inline-block" />Contratadas</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />Mes actual</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-500 inline-block" />Anteriores</span>
          </div>
        </div>
      </div>

    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, sub }) {
  const colors = {
    green: 'bg-green-50 text-green-600 border-green-100',
    blue: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  };
  const iconColors = {
    green: 'text-green-600 bg-green-100',
    blue: 'text-emerald-600 bg-emerald-100',
    purple: 'text-purple-600 bg-purple-100',
    red: 'text-red-600 bg-red-100',
    emerald: 'text-emerald-600 bg-emerald-100',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${iconColors[color]}`}>
          <Icon size={16} />
        </div>
        <span className="text-xs font-medium opacity-75 uppercase">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}
